import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import useWindowSize from "../../Globals/Helpers/useWindowSize";
import SearchBar from '../../Globals/CustomComponents/SearchBar';
import { getCurrentUser, logout } from '../../Api/index.ts';
import logo from "../../Assets/images/NewBookloopLogo.png";

const pages = ['Home', 'Find a textbook', 'Sell a textbook', 'How it works', 'Contact'];
const routes = ['/', '/listings', '/my-listings', '/how-it-works', '/contact'];

export default function Navbar() {
  const { width } = useWindowSize();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = width <= 640;
  
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await getCurrentUser();
        setUser(response.user);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = (route) => {
    setAnchorElNav(null);
    if (route) navigate(route);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      handleCloseUserMenu();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDashboard = () => {
    handleCloseUserMenu();
    navigate('/dashboard');
  };

  const handleProfile = () => {
    handleCloseUserMenu();
    navigate('/profile');
  };

  return (
    <>
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: 'none',
          height: { xs: '60px', md: '80px' },
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '1px solid #E0E0E0'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters
            sx={{
              minHeight: { xs: '60px', md: '80px' },
              padding: { xs: '0 16px', md: '0 40px' }
            }}
          >
            {/* Logo - Mobile */}
            <Box 
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                ml: -2,
                flexGrow: 0,
                marginRight: 'auto',
              }}
            >
              <img 
                src={logo} 
                alt="Logo" 
                onClick={() => navigate('/')} 
                style={{ height: '55px', cursor: 'pointer' }}
              />
            </Box>

            {/* Logo - Desktop */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 2, ml: -4, height: '80px' }}>
              <img 
                src={logo} 
                alt="Logo" 
                onClick={() => navigate('/')} 
                style={{ height: '100%', cursor: 'pointer' }}
              />
            </Box>

            {/* Search Bar - Desktop */}
            {['/listings', '/my-listings', '/how-it-works', '/contact'].includes(location.pathname) && !isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                <SearchBar 
                  onSearch={(searchValue) => {
                    if (window.handleSearch) {
                      window.handleSearch(searchValue);
                    }
                  }}
                  isMobile={false}
                />
              </Box>
            )}

            {/* Desktop menu */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end', mr: -6 }}>
              {pages.map((page, index) => (
                <Button
                  key={page}
                  onClick={() => handleCloseNavMenu(routes[index])}
                  sx={{ 
                    my: 2, 
                    color: '#000', 
                    display: 'block',
                    textTransform: 'none',
                    fontSize: '16px',
                    mx: 1
                  }}
                >
                  {page}
                </Button>
              ))}
              
              {!loading && (
                user ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <IconButton
                      onClick={handleOpenUserMenu}
                      sx={{ p: 0 }}
                    >
                      <Avatar sx={{ 
                        bgcolor: '#6B89FF',
                        width: 40,
                        height: 40,
                        fontSize: '1rem'
                      }}>
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </Avatar>
                    </IconButton>
                    <Menu
                      sx={{ mt: '45px' }}
                      id="menu-appbar"
                      anchorEl={anchorElUser}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      open={Boolean(anchorElUser)}
                      onClose={handleCloseUserMenu}
                    >
                      <MenuItem onClick={handleDashboard}>
                        <Typography textAlign="center">Dashboard</Typography>
                      </MenuItem>
                      <MenuItem onClick={handleProfile}>
                        <Typography textAlign="center">Profile</Typography>
                      </MenuItem>
                      <MenuItem onClick={handleLogout}>
                        <Typography textAlign="center" sx={{ color: '#e74c3c' }}>Logout</Typography>
                      </MenuItem>
                    </Menu>
                  </Box>
                ) : (
                  <Button
                    sx={{
                      my: 2,
                      backgroundColor: '#6B89FF',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#5A75E6',
                      },
                      ml: 2,
                      textTransform: 'none',
                      fontSize: '16px',
                      borderRadius: '14px',
                      height: '40px',
                      px: 2
                    }}
                    onClick={() => navigate('/login')}
                  >
                    Login with school email
                  </Button>
                )
              )}
            </Box>

            {/* Mobile menu */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, mr: -3  }}>
              <IconButton
                size="large"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                sx={{ color: '#000' ,
                  '& .MuiSvgIcon-root': {  // Target the MenuIcon
                    fontSize: '30px'  // Increased from default (24px)
                  }}}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElNav)}
                onClose={() => handleCloseNavMenu()}
                sx={{
                  display: { xs: 'block', md: 'none' }
                }}
              >
                {!loading && (
                  user ? (
                    <>
                      <MenuItem onClick={() => handleCloseNavMenu('/dashboard')}>
                        <Typography textAlign="center" sx={{ color: '#000' }}>Dashboard</Typography>
                      </MenuItem>
                      <MenuItem onClick={() => handleCloseNavMenu('/profile')}>
                        <Typography textAlign="center" sx={{ color: '#000' }}>Profile</Typography>
                      </MenuItem>
                      <MenuItem onClick={handleLogout}>
                        <Typography textAlign="center" sx={{ color: '#e74c3c' }}>Logout</Typography>
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem onClick={() => handleCloseNavMenu('/login')}>
                      <Typography textAlign="center" sx={{ color: '#000' }}>Login</Typography>
                    </MenuItem>
                  )
                )}
                {pages.map((page, index) => (
                  <MenuItem key={page} onClick={() => handleCloseNavMenu(routes[index])}>
                    <Typography textAlign="center" sx={{ color: '#000' }}>{page}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {/* Mobile SearchBar below header */}
      {['/listings', '/my-listings', '/how-it-works', '/contact'].includes(location.pathname) && isMobile && (
        <Box sx={{
          width: '100%',
          background: 'white',
          borderBottom: '1px solid #E0E0E0',
          px: 2,
          py: 1,
          zIndex: 10,
          position: 'relative',
        }}>
          <SearchBar 
            onSearch={(searchValue) => {
              if (window.handleSearch) {
                window.handleSearch(searchValue);
              }
            }}
            isMobile={true}
          />
        </Box>
      )}
    </>
  );
}

